<script setup lang="ts">
import { ref, watch } from 'vue'
import { 
  VueFlow, 
  useVueFlow, 
  Handle, 
  Position, 
  BaseEdge, 
  EdgeLabelRenderer, 
  getBezierPath
} from '@vue-flow/core'
import { Background } from '@vue-flow/background'
import { Controls } from '@vue-flow/controls'
import '@vue-flow/core/dist/style.css'
import '@vue-flow/core/dist/theme-default.css'
import '@vue-flow/controls/dist/style.css'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Square, Trash2, Copy, Check, RefreshCw, Code2, PlusSquare } from 'lucide-vue-next'
import BaseVisualEditor from './BaseVisualEditor.vue'
import MermaidCodeEditor from './MermaidCodeEditor.vue'
import MermaidLivePreview from './MermaidLivePreview.vue'

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  (e: 'update:open', value: boolean): void
  (e: 'save', mermaidCode: string): void
}>()

const nodes = ref<any[]>([])
const edges = ref<any[]>([])
const showCode = ref(false)
const copied = ref(false)
const editableCode = ref('')
const isParsing = ref(false)

// Inicializar lienzo
const initNodes = () => {
  nodes.value = [{ 
    id: 'n-Animal', type: 'classNode', position: { x: 200, y: 50 }, 
    data: { name: 'Animal', annotation: 'interface', note: 'Esta es la clase base', properties: ['+int age', '+String gender'], methods: ['+isMammal() bool', '+mate()'] } 
  }]
  edges.value = []
}

// Inicializar al abrir
watch(() => props.open, (val) => {
  if (val) {
    const editingCode = localStorage.getItem('editingDiagramCode');
    if (editingCode) {
      setTimeout(() => {
        editableCode.value = editingCode;
        localStorage.removeItem('editingDiagramCode');
        syncToVisual();
      }, 300);
      return;
    }
  }

  if (val && nodes.value.length === 0) initNodes()
}, { immediate: true })

const { onConnect, removeNodes } = useVueFlow()

const handleConnect = (params: any) => {
  edges.value.push({
    id: `e-${params.source}-${params.target}-${Date.now()}`,
    source: params.source,
    target: params.target,
    type: 'custom',
    data: { 
      relation: '<|--', // Herencia por defecto
      label: '',
      cardinalitySource: '',
      cardinalityTarget: ''
    }
  })
}

const addClassNode = () => {
  const id = `n-Clase_${Date.now()}`
  nodes.value.push({
    id,
    type: 'classNode',
    position: { x: Math.random() * 200 + 100, y: Math.random() * 200 + 100 },
    data: { name: `Clase_${Math.floor(Math.random()*1000)}`, annotation: '', note: '', properties: [], methods: [] }
  })
}

const deleteNode = (id: string) => {
  nodes.value = nodes.value.filter(n => n.id !== id)
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id)
}

const deleteEdge = (id: string) => {
  edges.value = edges.value.filter(e => e.id !== id)
}

const generateCodeString = () => {
  let mermaid = '\n```mermaid\n'
  
  // Guardar metadata de posiciones (oculto en comentario Mermaid)
  const positions: Record<string, any> = {}
  nodes.value.forEach(node => {
    // node.id is something like n-Animal, but wait! The ID in DiagramEditorDialog is `n-${name}`.
    positions[node.id] = { x: Math.round(node.position.x), y: Math.round(node.position.y) }
  })
  mermaid += `%% {"positions": ${JSON.stringify(positions)}}\n`
  
  mermaid += 'classDiagram\n'
  
  // Generar relaciones (Edges)
  edges.value.forEach(edge => {
    const rel = edge.data?.relation || '<|--'
    const lbl = edge.data?.label?.trim()
    const cardS = edge.data?.cardinalitySource?.trim()
    const cardT = edge.data?.cardinalityTarget?.trim()
    
    const sourceNode = nodes.value.find(n => n.id === edge.source)
    const targetNode = nodes.value.find(n => n.id === edge.target)
    
    if (sourceNode && targetNode) {
      let line = `  ${sourceNode.data.name}`
      if (cardS) line += ` "${cardS}"`
      line += ` ${rel}`
      if (cardT) line += ` "${cardT}"`
      line += ` ${targetNode.data.name}`
      if (lbl) line += ` : ${lbl}`
      
      mermaid += line + '\n'
    }
  })

  // Generar las clases y sus miembros (Nodos)
  nodes.value.forEach(node => {
    mermaid += `  class ${node.data.name}{\n`
    if (node.data.annotation && node.data.annotation.trim()) {
      mermaid += `    <<${node.data.annotation.trim()}>>\n`
    }
    node.data.properties.forEach((prop: string) => {
      if (prop.trim()) mermaid += `    ${prop.trim()}\n`
    })
    node.data.methods.forEach((met: string) => {
      if (met.trim()) mermaid += `    ${met.trim()}\n`
    })
    mermaid += `  }\n`
    if (node.data.note && node.data.note.trim()) {
      mermaid += `  note for ${node.data.name} "${node.data.note.trim()}"\n`
    }
  })

  mermaid += '```\n'
  return mermaid
}

// Escuchar cambios en el UI visual para actualizar el código texto
watch([nodes, edges], () => {
  if (isParsing.value) return
  editableCode.value = generateCodeString()
}, { deep: true, immediate: true })

// Parsear Mermaid a VueFlow
const syncToVisual = () => {
  isParsing.value = true
  try {
    const lines = editableCode.value.split('\n')
    const parsedNodes = new Map() // name -> { properties, methods }
    const parsedEdges: any[] = []
    let savedPositions: Record<string, any> = {}

    const getNodeData = (name: string) => {
      if (!parsedNodes.has(name)) parsedNodes.set(name, { properties: [], methods: [], annotation: '', note: '' })
      return parsedNodes.get(name)
    }

    let inClassBlock: string | null = null

    lines.forEach(line => {
      line = line.trim()
      
      // Parsear JSON oculto de posiciones
      if (line.startsWith('%% {"positions":')) {
        try {
          savedPositions = JSON.parse(line.replace(/^%% /, '')).positions
        } catch (e) {
          console.error('Error parseando metadata de posiciones', e)
        }
        return
      }
      
      if (!line || line.startsWith('```') || line.startsWith('classDiagram') || line.startsWith('%%')) return

      // Relaciones
      const relations = ['<|--', '*--', 'o--', '-->', '..>', '..|>', '--']
      let foundRel = relations.find(r => line.includes(r))
      
      if (foundRel && !inClassBlock) {
        const parts = line.split(foundRel)
        let left = parts[0].trim()
        let right = parts[1].trim()
        
        let cardS = '', cardT = '', label = ''
        
        if (right.includes(':')) {
          const rParts = right.split(':')
          label = rParts[1].trim()
          right = rParts[0].trim()
        }
        
        let rightMatch = right.match(/^"([^"]+)"\s+(.+)$/)
        if (rightMatch) {
          cardT = rightMatch[1]
          right = rightMatch[2].trim()
        }
        
        let leftMatch = left.match(/^(.+)\s+"([^"]+)"$/)
        if (leftMatch) {
          left = leftMatch[1].trim()
          cardS = leftMatch[2]
        }

        getNodeData(left)
        getNodeData(right)
        
        parsedEdges.push({
          sourceName: left, targetName: right, relation: foundRel, cardinalitySource: cardS, cardinalityTarget: cardT, label
        })
        return
      }

      // Bloques de clase: class Nombre {
      if (line.startsWith('class ') && line.endsWith('{')) {
        inClassBlock = line.substring(6, line.length - 1).trim()
        getNodeData(inClassBlock)
        return
      }

      if (inClassBlock && line === '}') {
        inClassBlock = null
        return
      }

      if (inClassBlock) {
        const node = getNodeData(inClassBlock)
        if (line.startsWith('<<') && line.endsWith('>>')) {
          node.annotation = line.substring(2, line.length - 2).trim()
        } else if (line.includes('(') && line.includes(')')) {
          node.methods.push(line)
        } else {
          node.properties.push(line)
        }
        return
      }
      
      const noteMatch = line.match(/^note\s+for\s+([^\s]+)\s+"([^"]+)"$/)
      if (noteMatch) {
         const node = getNodeData(noteMatch[1])
         node.note = noteMatch[2]
         return
      }

      // Linea suelta: ClassName : +prop
      if (line.includes(':') && !foundRel) {
        const parts = line.split(':')
        const name = parts[0].trim()
        if (name !== 'class') {
          const val = parts[1].trim()
          const node = getNodeData(name)
          if (val.includes('(') && val.includes(')')) node.methods.push(val)
          else node.properties.push(val)
        }
      }
    })

    // Reconstruir visuales
    const newNodes: any[] = []
    let currentX = 100, currentY = 100
    
    parsedNodes.forEach((data, name) => {
      const nodeId = `n-${name}`
      // Intentar mantener posición si existía en visual o usar la guardada en el comment
      const existing = nodes.value.find(n => n.id === nodeId)
      let pos = existing ? existing.position : { x: currentX, y: currentY }
      
      if (!existing && savedPositions[nodeId]) {
        pos = { x: savedPositions[nodeId].x, y: savedPositions[nodeId].y }
      } else if (!existing) { 
        currentX += 30; currentY += 30 
      }
      
      newNodes.push({
        id: `n-${name}`, type: 'classNode', position: pos,
        data: { name, properties: data.properties, methods: data.methods, annotation: data.annotation, note: data.note }
      })
    })

    const newEdges = parsedEdges.map((e, i) => ({
      id: `e-${e.sourceName}-${e.targetName}-${i}`,
      source: `n-${e.sourceName}`,
      target: `n-${e.targetName}`,
      type: 'custom',
      data: { relation: e.relation, cardinalitySource: e.cardinalitySource, cardinalityTarget: e.cardinalityTarget, label: e.label }
    }))

    nodes.value = newNodes
    edges.value = newEdges
  } catch (e) {
    console.error('Error parseando Mermaid:', e)
  }
  
  // Pequeño delay para reactivar el watcher sin loop
  setTimeout(() => isParsing.value = false, 100)
}

let debounceTimer: ReturnType<typeof setTimeout>
const onCodeChange = () => {
  clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    syncToVisual()
  }, 400) // 400ms delay para auto-sincronizar mientras escribe
}

const generateMermaid = () => {
  emit('save', editableCode.value)
  emit('update:open', false)
  initNodes()
}

const copyToClipboard = async () => {
  try {
    await navigator.clipboard.writeText(editableCode.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (err) {
    console.error('Failed to copy text', err)
  }
}
</script>

<template>
  <BaseVisualEditor
    :open="open"
    @update:open="$emit('update:open', $event)"
    title="Diagrama de Clases (UML)"
    description="Diseña la estructura orientada a objetos visualmente. Añade clases, propiedades, métodos y relaciones (herencia, composición, etc.)."
    :showCode="showCode"
    @update:showCode="showCode = $event"
    :copied="copied"
    @copy="copyToClipboard"
    @save="generateMermaid"
  >
    <template #icon>
      <Square class="w-5 h-5 text-primary" />
    </template>

    <template #toolbar>
      <Button size="sm" @click="addClassNode"><PlusSquare class="w-4 h-4 mr-2" /> Añadir Clase</Button>
    </template>

    <template #canvas>
      <VueFlow 
        v-model:nodes="nodes" 
        v-model:edges="edges"
        @connect="handleConnect"
        :default-zoom="1"
      >
        <Background />
        <Controls />

        <!-- Node Template para Clases -->
        <template #node-classNode="nodeProps">
          <div class="relative group border-2 border-primary rounded-md bg-card shadow-sm min-w-[200px] flex flex-col font-mono text-sm overflow-hidden">
            <!-- Header (Class Name & Annotation) -->
            <div class="bg-primary/10 p-2 text-center border-b border-primary flex flex-col gap-1">
              <input v-model="nodeProps.data.annotation" class="bg-transparent text-center w-full focus:outline-none text-xs text-primary italic" placeholder="<<annotation>>" />
              <input v-model="nodeProps.data.name" class="bg-transparent text-center w-full focus:outline-none font-bold" placeholder="ClassName" />
            </div>
            
            <!-- Note -->
            <div class="p-1 border-b border-primary bg-yellow-100/50 dark:bg-yellow-900/20">
              <textarea v-model="nodeProps.data.note" class="bg-transparent text-[10px] w-full focus:outline-none resize-none text-center" placeholder="Añadir nota de clase..." rows="1"></textarea>
            </div>
            
            <!-- Properties -->
            <div class="p-2 min-h-[40px] border-b border-primary flex flex-col gap-1 bg-card">
              <div v-for="(prop, i) in nodeProps.data.properties" :key="i" class="flex items-center group/item">
                <input v-model="nodeProps.data.properties[i]" class="bg-transparent text-xs w-full focus:outline-none focus:bg-muted/50 rounded px-1" placeholder="+type name" />
                <button @click="nodeProps.data.properties.splice(i, 1)" class="text-destructive opacity-0 group-hover/item:opacity-100"><Trash2 class="w-3 h-3" /></button>
              </div>
              <button @click="nodeProps.data.properties.push('+newProp')" class="text-[10px] text-muted-foreground hover:text-primary text-left mt-1">+ Añadir propiedad</button>
            </div>

            <!-- Methods -->
            <div class="p-2 min-h-[40px] flex flex-col gap-1 bg-card">
              <div v-for="(method, i) in nodeProps.data.methods" :key="i" class="flex items-center group/item">
                <input v-model="nodeProps.data.methods[i]" class="bg-transparent text-xs w-full focus:outline-none focus:bg-muted/50 rounded px-1" placeholder="+method()" />
                <button @click="nodeProps.data.methods.splice(i, 1)" class="text-destructive opacity-0 group-hover/item:opacity-100"><Trash2 class="w-3 h-3" /></button>
              </div>
              <button @click="nodeProps.data.methods.push('+newMethod()')" class="text-[10px] text-muted-foreground hover:text-primary text-left mt-1">+ Añadir método</button>
            </div>

            <!-- Handles para conexiones (4 lados) -->
            <Handle type="target" :position="Position.Top" id="top" />
            <Handle type="source" :position="Position.Bottom" id="bottom" />
            <Handle type="target" :position="Position.Left" id="left" />
            <Handle type="source" :position="Position.Right" id="right" />

            <!-- Delete Button -->
            <div class="absolute -top-3 -right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-20">
              <button @click.stop="deleteNode(nodeProps.id)" class="bg-destructive text-white rounded-full w-6 h-6 flex items-center justify-center hover:scale-110 shadow-sm" title="Eliminar Clase">×</button>
            </div>
          </div>
        </template>

        <!-- Edge Template para Relaciones -->
        <template #edge-custom="edgeProps">
          <BaseEdge 
            :id="edgeProps.id" 
            :path="getBezierPath(edgeProps)[0]" 
            class="vue-flow__edge-path" 
            :style="{ strokeWidth: 2, stroke: 'hsl(var(--primary))' }"
          />
          <EdgeLabelRenderer>
            <div
              :style="{
                position: 'absolute',
                transform: `translate(-50%, -50%) translate(${getBezierPath(edgeProps)[1]}px,${getBezierPath(edgeProps)[2]}px)`,
                pointerEvents: 'all',
              }"
              class="nodrag nopan bg-card border shadow-md rounded p-2 group z-10 flex flex-col gap-2 w-[180px]"
            >
              <!-- Configuración de Cardinalidad y Relación -->
              <div class="flex items-center gap-1">
                <input v-model="edgeProps.data.cardinalitySource" class="w-8 text-center text-xs border rounded bg-background p-1 focus:ring-1 focus:ring-primary outline-none" title="Cardinalidad Origen" placeholder="1" />
                
                <select v-model="edgeProps.data.relation" class="flex-1 text-xs border rounded p-1 bg-background focus:ring-1 focus:ring-primary outline-none" title="Tipo de Relación">
                  <option value="<|--">Herencia &lt;|--</option>
                  <option value="*--">Composición *--</option>
                  <option value="o--">Agregación o--</option>
                  <option value="-->">Asociación --&gt;</option>
                  <option value="..>">Dependencia ..&gt;</option>
                  <option value="..|>">Realización ..|&gt;</option>
                  <option value="--">Enlace --</option>
                </select>
                
                <input v-model="edgeProps.data.cardinalityTarget" class="w-8 text-center text-xs border rounded bg-background p-1 focus:ring-1 focus:ring-primary outline-none" title="Cardinalidad Destino" placeholder="*" />
              </div>
              
              <!-- Etiqueta de la relación (opcional) -->
              <input 
                v-model="edgeProps.data.label" 
                class="w-full bg-background text-center border rounded p-1 text-xs text-foreground focus:ring-1 focus:ring-primary outline-none"
                placeholder="Etiqueta (opcional)..."
              />

              <!-- Botón eliminar -->
              <button 
                @click="deleteEdge(edgeProps.id)" 
                class="absolute -top-2 -right-2 bg-destructive text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                title="Eliminar Conexión"
              >×</button>
            </div>
          </EdgeLabelRenderer>
        </template>
      </VueFlow>
    </template>

    <template #code-panel>
      <div class="text-sm font-bold p-2 bg-muted-foreground/10 text-muted-foreground flex items-center justify-between border-b shrink-0">
        Mermaid (Sincronizado)
        <Button size="sm" variant="default" class="h-7 text-xs" @click="syncToVisual">
          <RefreshCw class="w-3 h-3 mr-2" />
          Sincronizar a Visual
        </Button>
      </div>
      <MermaidCodeEditor v-model="editableCode" class="flex-1 min-h-0" />
    </template>

    <template #live-preview>
      <MermaidLivePreview :code="editableCode" />
    </template>
  </BaseVisualEditor>
</template>

<style>
/* Estilos del editor (optimizados para Clases) */
.vue-flow__node-classNode {
  /* Anula estilos por defecto molestos */
}
.vue-flow__handle {
  width: 12px;
  height: 12px;
  background: hsl(var(--primary)) !important;
  border-radius: 50%;
  border: 2px solid hsl(var(--background));
}
.vue-flow__edge-path {
  stroke: hsl(var(--primary));
  stroke-width: 2;
}
/* Estilo para los selectores nativos en dark mode */
.dark select {
  background-color: hsl(var(--background));
  color: hsl(var(--foreground));
  border-color: hsl(var(--border));
}
</style>
